import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, money, formatDate, downloadPdf } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import { useLeviWorkspaceHint } from "@/lib/leviWorkspace";
import { Mark } from "@/components/Logo";
import CustomerPreview from "@/components/CustomerPreview";
import SharingCard from "@/components/SharingCard";
import { Send, FileSignature, ArrowLeft, Check, Printer, Link2, Languages, Eye, Mail, Download } from "lucide-react";
import { isAllowance, isTm, deriveDisclosureTotals } from "@/lib/disclosure";
import { newRequestKey, localizeSendError } from "@/lib/emailSend";

export const ProposalsList = () => {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/proposals").then((r) => setItems(r.data)); }, []);
  return (
    <DashboardLayout title={t("pages.proposals.title")} subtitle={t("pages.proposals.subtitle")}>
      {items.length === 0 ? (
        <div className="jc-card p-12 text-center">
          <div className="font-display text-xl">{t("proposals_form.list_empty_title")}</div>
          <p className="text-sm text-[var(--jc-text-soft)] mt-2">{t("proposals_form.list_empty_body")}</p>
          <Link to="/app/estimates" className="inline-flex items-center mt-5 px-4 py-2 rounded-md jc-btn-blue text-sm font-semibold">{t("proposals_form.list_empty_action")}</Link>
        </div>
      ) : (
        <div className="jc-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--jc-bg)] text-[var(--jc-text-soft)]">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">{t("proposals_form.col_customer")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("proposals_form.col_total")}</th>
                <th className="text-left px-5 py-3 font-semibold">{t("proposals_form.col_status")}</th>
                <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">{t("proposals_form.col_created")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--jc-border)]">
              {items.map((p) => (
                <tr key={p.id} className="hover:bg-[var(--jc-bg)]">
                  <td className="px-5 py-3 font-semibold">
                    <Link to={`/app/proposals/${p.id}`} data-testid={`proposal-row-${p.id}`}>{p.snapshot?.customer?.name || "—"}</Link>
                  </td>
                  <td className="px-5 py-3 font-mono-jc">{money(p.snapshot?.totals?.total)}</td>
                  <td className="px-5 py-3"><span className="text-xs px-2 py-1 rounded-full bg-[var(--jc-border)]">{t(`proposals_form.status_${p.status}`) === `proposals_form.status_${p.status}` ? p.status : t(`proposals_form.status_${p.status}`)}</span></td>
                  <td className="px-5 py-3 hidden md:table-cell text-[var(--jc-text-soft)]">{formatDate(p.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  );
};

export const ProposalDetail = () => {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { lang, t } = useLang();
  // Phase 5A · Publish current-page workspace hint.
  useLeviWorkspaceHint("proposal_detail", {
    open_document_type: "proposal",
    open_document_id: pid,
    current_section: "proposal_detail",
  });
  const [p, setP] = useState(null);
  const [contractLang, setContractLang] = useState(lang);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);
  useEffect(() => {
    api.get(`/proposals/${pid}`).then((r) => {
      setP(r.data);
      // Pre-select contract output language to match proposal's saved language
      if (r.data?.language) setContractLang(r.data.language);
    });
  }, [pid]);

  if (!p) return <DashboardLayout title={t("pages.proposals.detail_loading_title")} subtitle={t("pages.proposals.detail_loading_subtitle")}><div /></DashboardLayout>;

  const approve = async () => {
    try {
      const { data } = await api.post(`/proposals/${pid}/approve`);
      setP(data);
      toast.success(t("proposals_form.toast_approved"));
    } catch { toast.error(t("proposals_form.toast_approve_failed")); }
  };

  const makeContract = async () => {
    try {
      const { data } = await api.post("/contracts", { proposal_id: pid, language: contractLang });
      toast.success(t("proposals_form.toast_contract_created"));
      navigate(`/app/contracts/${data.id}`);
    } catch { toast.error(t("proposals_form.toast_contract_failed")); }
  };

  const copyPublicLink = async () => {
    const url = `${window.location.origin}/p/${p.public_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("proposals_form.toast_public_link_copied"));
    } catch {
      window.prompt(t("proposals_form.prompt_copy_link"), url);
    }
  };

  const emailToCustomer = async () => {
    if (sendingRef.current) return;
    const defaultEmail = p.snapshot?.customer?.email || "";
    const to = window.prompt(t("proposals_form.prompt_email_to"), defaultEmail);
    if (!to) return;
    const request_key = newRequestKey();
    sendingRef.current = true;
    setSending(true);
    try {
      const { data } = await api.post(`/email/proposal/${pid}`, { to_email: to, request_key });
      const label = data.mock ? t("proposals_form.toast_email_queued") : t("proposals_form.toast_email_sent");
      toast.success(`${label} — ${to}`);
    } catch (e) {
      toast.error(localizeSendError(e, t, "proposals_form.toast_email_failed"));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const downloadProposalPdf = async () => {
    try {
      const filename = await downloadPdf(`/proposals/${pid}/pdf`, "proposal.pdf");
      toast.success(t("proposals_form.toast_downloaded", filename));
    } catch {
      toast.error(t("proposals_form.toast_pdf_failed"));
    }
  };

  const s = p.snapshot || {};
  const totals = s.totals || {};
  // Phase 2 Step 4: shared helper handles legacy snapshots that predate
  // Phase-2-Step-1 (no `tm_estimate` in persisted totals).
  const { tmEstimate } = deriveDisclosureTotals(s);

  return (
    <DashboardLayout
      title={`${t("proposals_form.detail_title_prefix")} · ${s.customer?.name || ""}`}
      subtitle={t(`proposals_form.status_${p.status}`) === `proposals_form.status_${p.status}` ? p.status : t(`proposals_form.status_${p.status}`)}
      actions={
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="text-sm text-[var(--jc-text-soft)] inline-flex items-center gap-1"><ArrowLeft size={14}/> {t("proposals_form.back")}</button>
          <button
            onClick={() => setPreviewOpen(true)}
            data-testid="proposal-preview-customer-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)] hover:bg-[var(--jc-bg)]"
          >
            <Eye size={14}/> {t("proposals_form.preview_customer")}
          </button>
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)]"><Printer size={14}/> {t("proposals_form.print")}</button>
          <button onClick={downloadProposalPdf} data-testid="proposal-download-pdf-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md border border-[var(--jc-border-strong)]"><Download size={14}/> {t("proposals_form.download_pdf")}</button>
          {p.status !== "approved" ? (
            <button onClick={approve} data-testid="proposal-approve-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-blue"><Check size={14}/> {t("proposals_form.mark_approved")}</button>
          ) : (
            <>
              <div className="hidden sm:inline-flex items-center gap-1 px-2 py-1.5 rounded-md border border-[var(--jc-border-strong)] text-xs" data-testid="contract-output-lang">
                <Languages size={13} className="text-[var(--jc-text-soft)]" />
                <span className="text-[var(--jc-text-soft)]">{t("proposals_form.contract_lang_label")}</span>
                <select
                  value={contractLang}
                  onChange={(e) => setContractLang(e.target.value)}
                  data-testid="contract-output-lang-select"
                  className="ml-1 bg-transparent font-semibold focus:outline-none"
                >
                  <option value="en">{t("proposals_form.lang_english")}</option>
                  <option value="es">{t("proposals_form.lang_spanish")}</option>
                </select>
              </div>
              <button onClick={makeContract} data-testid="proposal-make-contract-btn" className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-md jc-btn-primary"><FileSignature size={14}/> {t("proposals_form.draft_contract")}</button>
            </>
          )}
        </div>
      }
    >
      <div className="max-w-4xl mx-auto mb-4">
        <SharingCard
          kind="proposal"
          docId={pid}
          doc={p}
          onDocRefresh={() => api.get(`/proposals/${pid}`).then((r) => setP(r.data))}
        />
      </div>
      <div className="max-w-4xl mx-auto bg-white border border-[var(--jc-border)] rounded-sm shadow-[0_8px_30px_rgba(0,0,0,0.04)] p-10 print:shadow-none print:border-0">
        <header className="flex items-start justify-between border-b border-[var(--jc-border)] pb-6">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Mark size={32} />
              <div className="font-display text-lg">{s.company?.name || t("proposals_form.fallback_company")}</div>
            </div>
            <div className="overline">{t("proposals_form.header_overline")}</div>
            <h1 className="font-display text-3xl mt-1">{s.customer?.name}</h1>
            <div className="text-sm text-[var(--jc-text-soft)] mt-1">{s.customer?.address}</div>
          </div>
          <div className="text-right text-sm text-[var(--jc-text-soft)]">
            <div>{t("proposals_form.header_date")} {formatDate(p.created_at)}</div>
            <div>{t("proposals_form.header_status")} <span className="font-semibold text-[var(--jc-text)]">{t(`proposals_form.status_${p.status}`) === `proposals_form.status_${p.status}` ? p.status : t(`proposals_form.status_${p.status}`)}</span></div>
            {p.language && (
              <div className="mt-1">
                <span data-testid="proposal-lang-badge" className="inline-block px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wider bg-[var(--jc-border)] text-[var(--jc-text)]">
                  {p.language.toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </header>

        <section className="mt-6">
          <div className="overline mb-2">{t("proposals_form.section_scope")}</div>
          <p className="text-sm leading-relaxed">{s.scope}</p>
          {p.included_items?.length > 0 && (
            <>
              <div className="overline mt-5 mb-2">{t("proposals_form.section_included")}</div>
              <ul className="list-disc pl-6 text-sm space-y-1">{p.included_items.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </>
          )}
          {p.excluded_items?.length > 0 && (
            <>
              <div className="overline mt-5 mb-2">{t("proposals_form.section_excluded")}</div>
              <ul className="list-disc pl-6 text-sm space-y-1">{p.excluded_items.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </>
          )}
        </section>

        <section className="mt-6">
          <div className="overline mb-2">{t("proposals_form.section_pricing")}</div>
          <table className="w-full text-sm border border-[var(--jc-border)]">
            <thead className="bg-[var(--jc-bg)]">
              <tr>
                <th className="text-left px-3 py-2">{t("proposals_form.col_item")}</th>
                <th className="text-right px-3 py-2">{t("proposals_form.col_qty")}</th>
                <th className="text-right px-3 py-2">{t("proposals_form.col_unit")}</th>
                <th className="text-right px-3 py-2">{t("proposals_form.col_total")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--jc-border)]">
              {(s.line_items || []).filter((l) => !isTm(l)).map((l, i) => (
                <tr key={i}>
                  <td className="px-3 py-2">
                    <div className="font-semibold">
                      {l.name || "—"}
                      {isAllowance(l) && (
                        <span data-testid="proposal-allowance-badge" className="ml-2 inline-block align-middle px-1.5 py-0.5 rounded-sm text-[10px] font-bold tracking-wider uppercase bg-[var(--jc-bg)] border border-[var(--jc-border-strong)] text-[var(--jc-text-soft)]">{t("proposals_form.allowance_badge")}</span>
                      )}
                    </div>
                    <div className="text-xs text-[var(--jc-text-soft)]">{l.description}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono-jc">{l.quantity}</td>
                  <td className="px-3 py-2 text-right">{l.unit}</td>
                  <td className="px-3 py-2 text-right font-mono-jc">{money(l.total)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[var(--jc-bg)] text-sm">
              <tr><td colSpan={3} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)]">{t("proposals_form.tfoot_subtotal")}</td><td className="px-3 py-1.5 text-right font-mono-jc">{money(totals.subtotal)}</td></tr>
              <tr><td colSpan={3} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)]">{t("proposals_form.tfoot_tax")}</td><td className="px-3 py-1.5 text-right font-mono-jc">{money(totals.tax)}</td></tr>
              <tr><td colSpan={3} className="px-3 py-2 text-right font-semibold">{t("proposals_form.tfoot_total")}</td><td className="px-3 py-2 text-right font-mono-jc font-bold">{money(totals.total)}</td></tr>
              <tr><td colSpan={3} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)]">{t("proposals_form.tfoot_deposit")}</td><td className="px-3 py-1.5 text-right font-mono-jc">{money(totals.deposit)}</td></tr>
              <tr><td colSpan={3} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)]">{t("proposals_form.tfoot_balance")}</td><td className="px-3 py-1.5 text-right font-mono-jc">{money(totals.balance_due)}</td></tr>
              {tmEstimate > 0 && (
                <tr data-testid="proposal-tm-reserve-summary"><td colSpan={3} className="px-3 py-1.5 text-right text-[var(--jc-text-soft)] italic">{t("proposals_form.tm_reserve_prefix")} <span className="text-[10px] uppercase tracking-wider">{t("proposals_form.tm_reserve_suffix")}</span></td><td className="px-3 py-1.5 text-right font-mono-jc italic text-[var(--jc-text-soft)]">{money(tmEstimate)}</td></tr>
              )}
            </tfoot>
          </table>
          {(s.line_items || []).some(isAllowance) && (
            <p className="text-xs text-[var(--jc-text-soft)] mt-2 leading-relaxed">
              <span className="font-semibold">{t("proposals_form.allowance_note_prefix")}</span>{t("proposals_form.allowance_note_body")}
            </p>
          )}
        </section>

        {(s.line_items || []).some(isTm) && (
          <section className="mt-6" data-testid="proposal-tm-section">
            <div className="overline mb-2">{t("proposals_form.tm_section_heading")}</div>
            <table className="w-full text-sm border border-[var(--jc-border)]">
              <thead className="bg-[var(--jc-bg)]">
                <tr>
                  <th className="text-left px-3 py-2">{t("proposals_form.col_item")}</th>
                  <th className="text-right px-3 py-2">{t("proposals_form.col_qty")}</th>
                  <th className="text-right px-3 py-2">{t("proposals_form.col_unit")}</th>
                  <th className="text-right px-3 py-2">{t("proposals_form.tm_col_estimate")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--jc-border)]">
                {(s.line_items || []).filter(isTm).map((l, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2">
                      <div className="font-semibold">{l.name || "—"}</div>
                      <div className="text-xs text-[var(--jc-text-soft)]">{l.description}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-jc">{l.quantity}</td>
                    <td className="px-3 py-2 text-right">{l.unit}</td>
                    <td className="px-3 py-2 text-right font-mono-jc italic text-[var(--jc-text-soft)]">{money(l.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-[var(--jc-text-soft)] mt-2 leading-relaxed">{t("proposals_form.tm_footnote")}</p>
          </section>
        )}

        <section className="grid md:grid-cols-2 gap-6 mt-6 text-sm">
          <div>
            <div className="overline mb-1">{t("proposals_form.section_payment")}</div>
            <p>{p.payment_schedule || "—"}</p>
          </div>
          <div>
            <div className="overline mb-1">{t("proposals_form.section_timeline")}</div>
            <p>{p.timeline || "—"}</p>
          </div>
          <div className="md:col-span-2">
            <div className="overline mb-1">{t("proposals_form.section_next_steps")}</div>
            <p>{p.next_steps}</p>
          </div>
        </section>

        <footer className="mt-8 pt-6 border-t border-[var(--jc-border)] text-xs text-[var(--jc-text-soft)]">
          {s.disclaimer}
        </footer>
      </div>
      <CustomerPreview open={previewOpen} onOpenChange={setPreviewOpen} kind="proposal" doc={p} />
    </DashboardLayout>
  );
};
